import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import apiService from 'services/api'
import { EmailMessage, EmailThread } from 'models/emailIntegration'
import { EmailDetailActionBar } from './EmailDetailActionBar'
import { ThreadMessage } from './ThreadMessage'
import { EmailReplyBar } from './EmailReplyBar'
import { ComposePanel, ComposeMode, SendPayload } from './ComposePanel'
import { handleEmailError } from './handleEmailError'

/** Skeleton for loading state. */
function ThreadSkeleton() {
  return (
    <div className="flex flex-col gap-6 p-6">
      {[0, 1].map((i) => (
        <div key={i} className="flex gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
              <div className="ml-auto h-4 w-28 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="h-3 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="mt-4 space-y-2">
              <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-11/12 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-4/5 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

interface EmailDetailViewProps {
  thread: EmailThread
  threadIndex: number
  totalThreads: number
  pageSize: number
  pageStart: number
  companyName?: string
  onBack: () => void
  onNavigateThread: (delta: number) => void
}

/** Thread-based email detail view with action bar, messages, and reply bar. */
export function EmailDetailView({
  thread,
  threadIndex,
  totalThreads,
  pageSize,
  pageStart,
  companyName,
  onBack,
  onNavigateThread,
}: EmailDetailViewProps) {
  const api = apiService()
  const queryClient = useQueryClient()

  const { data: messages, isLoading } = useQuery({
    queryKey: ['thread-messages', thread.id],
    queryFn: ({ signal }) =>
      api.emailIntegration.getThreadMessages(thread.id, signal),
  })

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [composeMode, setComposeMode] = useState<ComposeMode | null>(null)
  const [replyToMessage, setReplyToMessage] = useState<
    EmailMessage | undefined
  >()

  // Auto-scroll to bottom when compose panel opens
  useEffect(() => {
    if (composeMode && scrollContainerRef.current) {
      // Small delay to let the compose panel render before scrolling
      requestAnimationFrame(() => {
        scrollContainerRef.current?.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: 'smooth',
        })
      })
    }
  }, [composeMode])

  const handleCompose = useCallback(
    (mode: ComposeMode, message?: EmailMessage) => {
      setComposeMode(mode)
      // If a specific message is provided (per-message action), use it;
      // otherwise default to the last message in the thread
      setReplyToMessage(message || messages?.[messages.length - 1])
    },
    [messages]
  )

  const handleComposeFromBar = useCallback(
    (mode: ComposeMode) => {
      handleCompose(mode, messages?.[messages.length - 1])
    },
    [handleCompose, messages]
  )

  const resetCompose = useCallback(() => {
    setComposeMode(null)
    setReplyToMessage(undefined)
  }, [])

  // Optimistic send: append message to cache immediately, fire API in background
  const sendMutation = useMutation({
    mutationFn: ({ formData, fromId }: SendPayload) =>
      api.emailIntegration.sendEmailMessage(fromId, formData),
    onMutate: async ({ optimisticMessage }: SendPayload) => {
      await queryClient.cancelQueries({
        queryKey: ['thread-messages', thread.id],
      })
      const previous = queryClient.getQueryData<EmailMessage[]>([
        'thread-messages',
        thread.id,
      ])
      queryClient.setQueryData<EmailMessage[]>(
        ['thread-messages', thread.id],
        (old) => [...(old || []), optimisticMessage]
      )
      return { previous }
    },
    onError: async (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ['thread-messages', thread.id],
          context.previous
        )
      }
      await handleEmailError(_err, api.emailIntegration)
    },
    onSuccess: () => {
      toast.success('Email sent successfully')
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['thread-messages', thread.id],
      })
    },
  })

  const handleSendSuccess = useCallback(
    (payload: SendPayload) => {
      resetCompose()
      sendMutation.mutate(payload)
    },
    [resetCompose, sendMutation]
  )

  return (
    <div className="h-full flex flex-col border border-[#EDEFF1] rounded-lg overflow-hidden">
      {/* Action bar */}
      <div className="px-4 pt-3 pb-3 border-b border-[#EDEFF1]">
        <EmailDetailActionBar
          threadIndex={threadIndex}
          totalThreads={totalThreads}
          pageSize={pageSize}
          pageStart={pageStart}
          onBack={onBack}
          onNavigateThread={onNavigateThread}
          onCompose={handleComposeFromBar}
        />
      </div>

      {/* Subject + company badge */}
      <div className="mt-3 mb-2 pl-[60px] pr-4">
        <h2 className="text-xl font-normal text-[#191D23]">{thread.subject}</h2>
        {companyName && (
          <span className="inline-flex items-center mt-1.5 px-1 py-0.5 text-xs font-medium text-[#06512F] bg-[#F4F7F5] border border-[#06512F] rounded leading-[14.4px] tracking-[0.09px]">
            {companyName}
          </span>
        )}
      </div>

      {/* Thread messages (scrollable) — compose panel is inside the scroll area */}
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 overflow-y-auto flex flex-col"
      >
        <div className="flex-shrink-0">
          {isLoading ? (
            <ThreadSkeleton />
          ) : (
            (messages || []).map((message: EmailMessage, index: number) => (
              <ThreadMessage
                key={message.id}
                message={message}
                isLast={!composeMode && index === (messages?.length ?? 0) - 1}
                onCompose={handleCompose}
              />
            ))
          )}
        </div>

        {/* Inline compose panel — mt-auto pushes it to the bottom on short threads */}
        {composeMode && (
          <div className="mt-auto flex-shrink-0">
            <ComposePanel
              key={`${composeMode}-${replyToMessage?.id}`}
              mode={composeMode}
              thread={thread}
              replyToMessage={replyToMessage}
              integrationEmail={thread.email_integration_email}
              onSend={handleSendSuccess}
              onDiscard={resetCompose}
            />
          </div>
        )}
      </div>

      {/* Reply bar (hidden when composing) */}
      {!composeMode && (
        <div className="px-4 pb-4">
          <EmailReplyBar onCompose={handleComposeFromBar} />
        </div>
      )}
    </div>
  )
}

/** Simplified detail view for emailOnlyMode (no thread context). */
interface EmailOnlyDetailViewProps {
  email: EmailMessage
}

export function EmailOnlyDetailView({ email }: EmailOnlyDetailViewProps) {
  const api = apiService()

  const { data: emailData, isLoading } = useQuery({
    queryKey: ['email-message', email.id],
    queryFn: ({ signal }) =>
      api.emailIntegration.getEmailMessageById(email.id, signal),
  })

  const displayData = emailData || email

  return (
    <div className="h-full flex flex-col border border-[#EDEFF1] rounded-lg overflow-hidden">
      {/* Single message in scrollable area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <ThreadSkeleton />
        ) : (
          <ThreadMessage message={displayData} isLast />
        )}
      </div>

      <div className="px-4 pb-4">
        <EmailReplyBar />
      </div>
    </div>
  )
}
